import { forwardRef } from 'react';
import { alpha, Box, Switch, Typography, useTheme } from '@mui/material';
import useControlled from '@mui/utils/useControlled';

import type { FeatureToggleCardProps } from './FeatureToggleCard.types';

/**
 * Resolves a dot-path like 'primary.main' against the MUI theme palette,
 * falling back to the raw string if it's already a CSS color.
 */
function resolveColor(colorPath: string, palette: Record<string, unknown>): string {
  const parts = colorPath.split('.');
  let current: unknown = palette;
  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return colorPath; // not a palette path — treat as raw CSS color
    }
  }
  return typeof current === 'string' ? current : colorPath;
}

export const FeatureToggleCard = forwardRef<HTMLDivElement, FeatureToggleCardProps>(
  function FeatureToggleCard(
    {
      icon,
      title,
      description,
      badge,
      checked: checkedProp,
      defaultChecked,
      onChange,
      disabled = false,
      action,
      color = 'primary.main',
      switchAriaLabel,
      sx,
    },
    ref,
  ) {
    const theme = useTheme();
    const resolvedColor = resolveColor(color, theme.palette as unknown as Record<string, unknown>);

    const hasToggle = action === undefined;

    const [value, setValue] = useControlled<boolean>({
      controlled: hasToggle ? checkedProp : undefined,
      default: hasToggle ? (defaultChecked ?? false) : false,
      name: 'FeatureToggleCard',
      state: 'checked',
    });

    const isActive = hasToggle && value;

    const handleToggle = () => {
      if (disabled) return;
      const next = !value;
      setValue(next);
      onChange?.(next);
    };

    return (
      <Box
        ref={ref}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          borderRadius: 1,
          border: '1px solid',
          borderColor: isActive ? resolvedColor : 'divider',
          bgcolor: isActive ? alpha(resolvedColor, 0.04) : 'transparent',
          opacity: disabled ? 0.5 : 1,
          transition: 'border-color 0.2s ease, background-color 0.2s ease',
          cursor: hasToggle && !disabled ? 'pointer' : 'default',
          ...(sx as Record<string, unknown> | undefined),
        }}
        onClick={hasToggle ? handleToggle : undefined}
        role={hasToggle ? 'switch' : undefined}
        aria-checked={hasToggle ? value : undefined}
      >
        {/* Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: 1.5,
            flexShrink: 0,
            bgcolor: isActive ? alpha(resolvedColor, 0.15) : 'action.hover',
            color: isActive ? resolvedColor : 'text.secondary',
            transition: 'background-color 0.2s ease, color 0.2s ease',
            '& svg': { fontSize: 20 },
          }}
        >
          {icon}
        </Box>

        {/* Text */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                color: isActive ? 'text.primary' : 'text.secondary',
              }}
            >
              {title}
            </Typography>
            {badge}
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        </Box>

        {/* Right side: toggle or action */}
        {hasToggle ? (
          <Switch
            checked={value}
            disabled={disabled}
            onChange={e => {
              e.stopPropagation();
              handleToggle();
            }}
            onClick={e => e.stopPropagation()}
            inputProps={{ 'aria-label': switchAriaLabel ?? `Toggle ${title}` }}
            sx={{
              flexShrink: 0,
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: resolvedColor,
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: resolvedColor,
              },
            }}
          />
        ) : (
          <Box sx={{ flexShrink: 0 }}>{action}</Box>
        )}
      </Box>
    );
  },
);
